import React, { useState, useEffect, useRef } from 'react';
import { ApiClient, AuthTokens, CreditCard, ConnectedAccount } from '../lib/ApiClient';
import BankProvider from './providers/BankProvider';

interface CreditCardSlot {
  id: string;
  connected: boolean;
  bank: string | null;
  accountDetails: string | null;
  selectedCard: string | null;
  availableCards: CreditCard[];
  connectedAccount?: ConnectedAccount;
  isConnecting: boolean;
  missingCardName?: string;
}

interface CreditCardConnectorProps {
  apiClient: ApiClient;
  authTokens: AuthTokens | null;
  setAuthTokens: (tokens: AuthTokens | null) => void;
  simulationId: string | null;
  onConnectionsChange: (connectedAndMappedCount: number, totalConnectedCount: number, unmappableCount: number) => void;
  setError: (error: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  onStartOver: () => void;
}

const CreditCardConnector: React.FC<CreditCardConnectorProps> = ({
  apiClient,
  authTokens,
  setAuthTokens,
  simulationId,
  onConnectionsChange,
  setError,
  isLoading,
  setIsLoading,
  onStartOver
}) => {
  const [creditCardSlots, setCreditCardSlots] = useState<CreditCardSlot[]>([]);
  const [activeSlotId, setActiveSlotId] = useState<string | null>(null);
  const [hasBankConnection, setHasBankConnection] = useState<boolean>(false);
  const activeSlotIdRef = useRef<string | null>(null);
  const simulationIdRef = useRef<string | null>(simulationId);

  // Update simulationIdRef whenever simulationId prop changes
  useEffect(() => {
    simulationIdRef.current = simulationId;
  }, [simulationId]);

  // Create initial slot
  const createInitialSlots = (): CreditCardSlot[] => {
    return [{
      id: 'bank_connect_slot',
      connected: false,
      bank: null,
      accountDetails: null,
      selectedCard: null,
      availableCards: [],
      isConnecting: false,
    }];
  };

  // Initialize slots and load existing data
  useEffect(() => {
    loadExistingData();
  }, [simulationId]);

  const loadExistingData = async () => {
    try {
      if (!simulationId) {
        setCreditCardSlots(createInitialSlots());
        return;
      }

      // Get all accounts from the simulation
      const allAccounts = await fetchAllAccountsFromSimulation();

      if (allAccounts.length > 0) {
        setHasBankConnection(true);
        await populateSlotsFromAccounts(allAccounts);
      } else {
        setCreditCardSlots(createInitialSlots());
      }
    } catch (err) {
      console.error('Error loading existing data:', err);
      setCreditCardSlots(createInitialSlots());
    }
  };

  const fetchAllAccountsFromSimulation = async () => {
    try {
      if (!simulationId) {
        console.error('No simulation ID available');
        return [];
      }

      const enrollmentsData = await apiClient.getSimulationEnrollments(simulationId);
      const enrollmentIds = enrollmentsData.enrollments?.map((e: any) => e.enrollment_id) || [];

      if (enrollmentIds.length === 0) {
        return [];
      }

      const accountPromises = enrollmentIds.map(enrollmentId =>
        apiClient.getBankAccountMappings(enrollmentId)
      );

      const allAccountsData = await Promise.all(accountPromises);
      const allAccounts: ConnectedAccount[] = [];

      allAccountsData.forEach(data => {
        const accounts = data.accounts || [];
        allAccounts.push(...accounts.filter((acc: ConnectedAccount) => acc.type === 'credit' && !acc.ignored));
      });

      return allAccounts;
    } catch (err) {
      console.error('Error fetching accounts from simulation:', err);
      return [];
    }
  };

  const populateSlotsFromAccounts = async (accounts: ConnectedAccount[]) => {
    const newSlots: CreditCardSlot[] = [];

    accounts.forEach((account) => {
      newSlots.push({
        id: `account_slot_${account.id}`,
        connected: true,
        bank: account.institution,
        accountDetails: `${account.type} •••• ${account.last_four}`,
        selectedCard: account.credit_card?.id || null,
        availableCards: account.potential_cards || [],
        connectedAccount: account,
        isConnecting: false,
      });
    });

    // Add the "+ Add more" slot at the end
    newSlots.push({
      id: 'add_more_slot',
      connected: false,
      bank: null,
      accountDetails: null,
      selectedCard: null,
      availableCards: [],
      isConnecting: false,
    });

    setCreditCardSlots(newSlots);
    await updateConnectionCount(newSlots);
  };

  const addAccountsToExistingSlots = async (newAccounts: ConnectedAccount[]) => {
    setCreditCardSlots(prev => {
      const existingAccountSlots = prev.filter(slot => slot.id !== 'add_more_slot');

      const newSlots = newAccounts.map(account => ({
        id: `account_slot_${account.id}`,
        connected: true,
        bank: account.institution,
        accountDetails: `${account.type} •••• ${account.last_four}`,
        selectedCard: account.credit_card?.id || null,
        availableCards: account.potential_cards || [],
        connectedAccount: account,
        isConnecting: false,
      }));

      const allSlots = [
        ...existingAccountSlots,
        ...newSlots,
        {
          id: 'add_more_slot',
          connected: false,
          bank: null,
          accountDetails: null,
          selectedCard: null,
          availableCards: [],
          isConnecting: false,
        }
      ];

      // Update connection count after state is set
      setTimeout(() => updateConnectionCount(allSlots), 0);
      return allSlots;
    });
  };

  const updateConnectionCount = async (slots: CreditCardSlot[]) => {
    const connectedAndMapped = slots.filter(slot =>
      slot.connected && slot.selectedCard
    ).length;
    const totalConnected = slots.filter(slot => slot.connected).length;
    const unmappable = slots.filter(slot => 
      slot.connected && slot.availableCards.length === 0
    ).length;
    
    // Check if all enrollments are ready for analysis
    let allEnrollmentsReady = false;
    try {
      if (simulationIdRef.current) {
        const enrollmentsData = await apiClient.getSimulationEnrollments(simulationIdRef.current);
        allEnrollmentsReady = enrollmentsData.all_ready_for_analysis || false;
      }
    } catch (err) {
      console.error('Error checking enrollment readiness:', err);
    }
    
    onConnectionsChange(connectedAndMapped, totalConnected, unmappable, allEnrollmentsReady);
  };

  const handleBankProviderSuccess = async (enrollmentId: string) => {
    const currentActiveSlotId = activeSlotIdRef.current;
    
    try {
      // Poll for completion
      await pollEnrollmentComplete(enrollmentId);
      
      // Get the new accounts
      const accountsData = await apiClient.getBankAccountMappings(enrollmentId);
      const creditAccounts = accountsData.accounts?.filter((acc: ConnectedAccount) => acc.type === 'credit' && !acc.ignored) || [];
      
      if (creditAccounts.length > 0) {
        setHasBankConnection(true);

        if (currentActiveSlotId === 'bank_connect_slot') {
          await populateSlotsFromAccounts(creditAccounts);
        } else {
          await addAccountsToExistingSlots(creditAccounts);
        }
      } else {
        setError('No credit card accounts found in this connection');
      }

      setActiveSlotId(null);
      activeSlotIdRef.current = null;
      setIsLoading(false);
      
      // Reset connecting state
      setCreditCardSlots(prev => prev.map(slot =>
        slot.id === currentActiveSlotId
          ? { ...slot, isConnecting: false }
          : slot
      ));
    } catch (err) {
      console.error('Error processing bank connection:', err);
      setError(`Failed to process bank connection: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setCreditCardSlots(prev => prev.map(slot =>
        slot.id === currentActiveSlotId
          ? { ...slot, isConnecting: false }
          : slot
      ));
      setActiveSlotId(null);
      activeSlotIdRef.current = null;
      setIsLoading(false);
    }
  };

  const handleBankProviderError = (error: string) => {
    console.error('Bank provider error:', error);
    setError(error);
    
    const currentActiveSlotId = activeSlotIdRef.current;
    setCreditCardSlots(prev => prev.map(slot =>
      slot.id === currentActiveSlotId
        ? { ...slot, isConnecting: false }
        : slot
    ));
    setActiveSlotId(null);
    activeSlotIdRef.current = null;
  };

  const pollEnrollmentComplete = async (enrollmentId: string) => {
    return new Promise<void>((resolve, reject) => {
      let pollAttempts = 0;
      const maxAttempts = 150;

      const checkStatus = async () => {
        try {
          pollAttempts++;
          
          const enrollmentsData = await apiClient.getSimulationEnrollments(simulationIdRef.current!);
          const enrollment = enrollmentsData.enrollments?.find((e: any) => e.enrollment_id === enrollmentId);

          if (enrollment) {
            // Check for business_accounts_syncd or completed status - accounts are ready for card selection
            if (enrollment.ready_for_card_selection) {
              resolve();
              return;
            }
            // Also handle error state - should stop polling
            if (enrollment.status === 'error') {
              reject(new Error('Enrollment failed during sync'));
              return;
            }
          }

          if (pollAttempts >= maxAttempts) {
            reject(new Error('Enrollment timeout - max attempts reached'));
            return;
          }

          setTimeout(checkStatus, 2000);
        } catch (err) {
          reject(err);
        }
      };

      checkStatus();

      setTimeout(() => {
        reject(new Error('Enrollment timeout'));
      }, 5 * 60 * 1000);
    });
  };

  const connectBankAccount = (slotId: string) => {
    setActiveSlotId(slotId);
    activeSlotIdRef.current = slotId;

    setCreditCardSlots(prev => prev.map(slot =>
      slot.id === slotId
        ? { ...slot, isConnecting: true }
        : slot
    ));
  };

  const selectCard = async (slotId: string, cardId: string) => {
    const slot = creditCardSlots.find(s => s.id === slotId);
    if (!slot?.connectedAccount) return;

    try {
      const data = await apiClient.updateBankAccountMapping(slot.connectedAccount.id, cardId);

      setCreditCardSlots(prev => {
        const newSlots = prev.map(s =>
          s.id === slotId
            ? {
                ...s,
                selectedCard: cardId,
                connectedAccount: {
                  ...s.connectedAccount!,
                  credit_card: data.bank_account.credit_card,
                  mapping_status: data.bank_account.mapping_status
                }
              }
            : s
        );
        updateConnectionCount(newSlots);
        return newSlots;
      });
    } catch (err) {
      console.error('Error selecting card:', err);
      setError('Failed to map credit card');
    }
  };

  const removeCreditCardSlot = async (slotId: string) => {
    const accountSlots = creditCardSlots.filter(slot => slot.id !== 'add_more_slot');
    if (accountSlots.length <= 1) return;

    const slotToRemove = creditCardSlots.find(slot => slot.id === slotId);
    if (!slotToRemove?.connectedAccount) return;

    try {
      await apiClient.ignoreBankAccount(slotToRemove.connectedAccount.id);
      
      setCreditCardSlots(prev => {
        const newSlots = prev.filter(slot => slot.id !== slotId);
        setTimeout(() => updateConnectionCount(newSlots), 0);
        return newSlots;
      });
    } catch (err) {
      console.error('Error ignoring bank account:', err);
      setError('Failed to remove bank account');
    }
  };

  const updateMissingCardName = (slotId: string, cardName: string) => {
    setCreditCardSlots(prev => prev.map(slot =>
      slot.id === slotId
        ? { ...slot, missingCardName: cardName }
        : slot
    ));
  };

  const submitMissingCard = async (slot: CreditCardSlot) => {
    if (!slot.missingCardName || slot.missingCardName.trim() === '') return;

    try {
      const enrollmentId = slot.connectedAccount?.enrollment_id;
      
      await apiClient.submitMissingCreditCard(
        slot.missingCardName.trim(),
        '',
        enrollmentId
      );
    } catch (err) {
      console.error('Error submitting missing card:', err);
    }
  };

  const startOver = () => {
    setCreditCardSlots(createInitialSlots());
    setActiveSlotId(null);
    activeSlotIdRef.current = null;
    setHasBankConnection(false);
    onConnectionsChange(0, 0, 0);
    onStartOver();
  };

  return (
    <div>
      {/* Start Over Button */}
      {hasBankConnection && (
        <div style={{
          textAlign: 'right',
          marginBottom: '20px'
        }}>
          <button
            onClick={startOver}
            style={{
              background: 'transparent',
              border: '1px solid #e17055',
              color: '#e17055',
              padding: '8px 16px',
              borderRadius: '15px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#e17055';
              e.currentTarget.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#e17055';
            }}
          >
            🔄 Start Over
          </button>
        </div>
      )}

      {/* Credit Cards Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '20px',
        margin: '30px 0'
      }}>
        {creditCardSlots.map((slot) => (
          <div key={slot.id} style={{
            background: slot.connected && slot.availableCards.length === 0 ? '#ffeaa7' : 
                       slot.connected ? '#f0f9f4' : '#f8f9fa',
            padding: '25px',
            borderRadius: '15px',
            border: `2px solid ${slot.connected && slot.availableCards.length === 0 ? '#fdcb6e' : 
                                slot.connected ? '#00b894' : 'transparent'}`,
            transition: 'all 0.3s',
            position: 'relative'
          }}>
            {/* Remove button */}
            {slot.id !== 'bank_connect_slot' && slot.id !== 'add_more_slot' && creditCardSlots.filter(s => s.id !== 'add_more_slot').length > 1 && (
              <button
                onClick={() => removeCreditCardSlot(slot.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#e17055',
                  cursor: 'pointer',
                  fontSize: '18px',
                  padding: '5px',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '10px',
                  right: '10px'
                }}
              >
                ×
              </button>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#636e72',
                background: '#ddd',
                padding: '5px 12px',
                borderRadius: '15px'
              }}>
                {slot.id === 'bank_connect_slot' ? 'Connect Banks' :
                 slot.id === 'add_more_slot' ? 'Add More' :
                 `${slot.bank || 'Bank'}`}
              </div>
            </div>

            {!slot.connected ? (
              (slot.id === 'bank_connect_slot' || slot.id === 'add_more_slot') && (
                <div onClick={() => connectBankAccount(slot.id)}>
                  <BankProvider
                    apiClient={apiClient}
                    authTokens={authTokens}
                    setAuthTokens={setAuthTokens}
                    simulationId={simulationId}
                    isConnecting={slot.isConnecting}
                    onSuccess={handleBankProviderSuccess}
                    onError={handleBankProviderError}
                    setIsLoading={setIsLoading}
                  />
                </div>
              )
            ) : (
              <>
                <div style={{ marginBottom: '15px', textAlign: 'left' }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#2d3436', marginBottom: '5px' }}>
                    {slot.bank}
                  </div>
                  <div style={{ fontSize: '12px', color: '#636e72', fontFamily: 'monospace' }}>
                    {slot.accountDetails}
                  </div>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  {slot.availableCards.length > 0 ? (
                    <>
                      <label style={{
                        display: 'block',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#2d3436',
                        marginBottom: '8px'
                      }}>
                        Select your specific card:
                      </label>
                      <select
                        value={slot.selectedCard || ''}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => selectCard(slot.id, e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: `2px solid ${slot.selectedCard ? '#00b894' : '#ddd'}`,
                          borderRadius: '8px',
                          fontSize: '14px',
                          background: 'white'
                        }}
                      >
                        <option value="">Choose your card...</option>
                        {slot.availableCards.map(card => (
                          <option key={card.id} value={card.id}>
                            {card.name}
                          </option>
                        ))}
                      </select>
                    </>
                  ) : (
                    <>
                      <label style={{
                        display: 'block',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#2d3436',
                        marginBottom: '8px'
                      }}>
                        We don't have cards for {slot.bank} yet:
                      </label>
                      <input
                        type="text"
                        placeholder="Enter your card name (e.g., Chase Sapphire Preferred)"
                        value={slot.missingCardName || ''}
                        onChange={(e) => updateMissingCardName(slot.id, e.target.value)}
                        onBlur={() => submitMissingCard(slot)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '2px solid #ddd',
                          borderRadius: '8px',
                          fontSize: '14px',
                          background: 'white'
                        }}
                      />
                      <div style={{
                        fontSize: '11px',
                        color: '#636e72',
                        marginTop: '5px',
                        fontStyle: 'italic'
                      }}>
                        ⚠️ This card won't be used in the simulation, but we'll add it for future users.
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Security Info */}
      <div style={{
        background: '#f8f9fa',
        padding: '20px',
        borderRadius: '10px',
        marginTop: '30px',
        borderLeft: '4px solid #00b894'
      }}>
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#2d3436', marginBottom: '10px' }}>
          🛡️ Your data is secure
        </div>
        <div style={{ fontSize: '14px', color: '#636e72', textAlign: 'left' }}>
          • We use bank-level encryption<br/>
          • We only read transaction data, never account balances<br/>
          • Your login credentials are never stored<br/>
          • You can disconnect anytime
        </div>
      </div>
    </div>
  );
};

export default CreditCardConnector;